import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

/** Resend email notifications — new-order alerts + daily pending summary.
 * API key is stored server-side (settings.email_notify_config); the UI only
 * ever sees a masked version. */
export const EmailNotifySection = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    api_key: "", recipient: "", sender: "", enabled: true,
    daily_summary_enabled: true, daily_summary_time: "07:00",
  });

  const fetchCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/notifications/email-config`, { headers: authHeader() });
      setCfg(res.data);
      setForm((f) => ({
        ...f,
        recipient: res.data.recipient,
        sender: res.data.sender,
        enabled: res.data.enabled,
        daily_summary_enabled: res.data.daily_summary_enabled,
        daily_summary_time: res.data.daily_summary_time,
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load email config");
    }
  }, []);

  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.recipient.trim()) { toast.error("Recipient email is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/notifications/email-config`, form, { headers: authHeader() });
      toast.success("Email notification settings saved");
      setForm((f) => ({ ...f, api_key: "" }));
      await fetchCfg();
    } catch (e2) {
      toast.error(e2.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/api/notifications/email-test`, {}, { headers: authHeader() });
      toast.success("Test email sent — check the inbox");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="p-5" data-testid="email-notify-section">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-brand-navy" />
          <h3 className="font-semibold text-brand-navy">Email notifications (Resend)</h3>
        </div>
        <Button
          size="sm"
          onClick={handleTest}
          disabled={testing || !cfg?.api_key_configured}
          data-testid="email-notify-test-btn"
          className="bg-brand-navy hover:bg-brand-navy-hover text-white"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          <span className="ml-1.5">Send test</span>
        </Button>
      </div>

      <div className="text-sm text-muted-foreground mb-4 space-y-1">
        <p>New-order alerts + a daily pending-orders summary, emailed to one fixed address.</p>
        <ol className="list-decimal list-inside space-y-0.5">
          <li>Register at <span className="font-mono">resend.com</span> → API Keys → Create API Key</li>
          <li>Paste the key below. Without a verified domain, the test sender <span className="font-mono">onboarding@resend.dev</span> can ONLY email the address that owns the Resend account — so register Resend with the same email you want to receive alerts at</li>
          <li>To use any other recipient/sender, verify your domain in Resend first</li>
        </ol>
        {cfg?.api_key_configured && (
          <p className="font-mono text-xs">Key: {cfg.api_key_masked} · {cfg.last_summary_date ? `Last summary: ${cfg.last_summary_date}` : "No summary sent yet"}</p>
        )}
      </div>

      <form onSubmit={handleSave} className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="resend-key">Resend API key {cfg?.api_key_configured && <span className="text-muted-foreground">(blank = keep current)</span>}</Label>
            <Input
              id="resend-key" type="password" autoComplete="new-password"
              placeholder="re_..."
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value })}
              data-testid="email-notify-apikey-input"
            />
          </div>
          <div>
            <Label htmlFor="notify-recipient">Recipient email</Label>
            <Input
              id="notify-recipient" type="email" required
              placeholder="you@example.com"
              value={form.recipient}
              onChange={(e) => setForm({ ...form, recipient: e.target.value })}
              data-testid="email-notify-recipient-input"
            />
          </div>
          <div>
            <Label htmlFor="notify-sender">Sender (from)</Label>
            <Input
              id="notify-sender"
              placeholder="Laundry System <onboarding@resend.dev>"
              value={form.sender}
              onChange={(e) => setForm({ ...form, sender: e.target.value })}
              data-testid="email-notify-sender-input"
            />
          </div>
          <div>
            <Label htmlFor="summary-time">Daily summary time (Brisbane)</Label>
            <Input
              id="summary-time" type="time"
              value={form.daily_summary_time}
              onChange={(e) => setForm({ ...form, daily_summary_time: e.target.value })}
              data-testid="email-notify-summary-time-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="email-notify-enabled-switch" />
            Enable email notifications
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={form.daily_summary_enabled} onCheckedChange={(v) => setForm({ ...form, daily_summary_enabled: v })} data-testid="email-notify-summary-switch" />
            Daily morning summary
          </label>
        </div>

        <Button type="submit" disabled={saving} data-testid="email-notify-save-btn" className="bg-brand-navy hover:bg-brand-navy-hover text-white">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}Save
        </Button>
      </form>
    </Card>
  );
};
